import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Spinner,
} from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { isApiError } from '@shared/api';
import { useAcceptInvite } from '@features/auth';

const schema = z
  .object({
    password: z.string().min(8, 'Минимум 8 символов'),
    confirm: z.string(),
    displayName: z.string().max(120).optional().or(z.literal('')),
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'Пароли не совпадают',
  });

type Values = z.infer<typeof schema>;

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const accept = useAcceptInvite();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  if (!token) {
    return (
      <Shell title="Приглашение недействительно">
        <p className="text-sm text-muted-foreground">
          В URL отсутствует параметр <code>token</code>. Откройте ссылку из письма-приглашения.
        </p>
        <Link to={ROUTES.login} className="text-sm text-primary hover:underline">
          На страницу входа
        </Link>
      </Shell>
    );
  }

  const serverError = accept.error
    ? isApiError(accept.error)
      ? accept.error.status === 410
        ? 'Срок действия приглашения истёк или оно отозвано'
        : accept.error.message
      : 'Что-то пошло не так, попробуйте ещё раз'
    : null;

  return (
    <Shell title="Принять приглашение">
      <form
        onSubmit={handleSubmit((v) =>
          accept.mutate({
            token,
            password: v.password,
            displayName: v.displayName || undefined,
          }),
        )}
        className="space-y-4"
      >
        <FormField label="Имя (опционально)" htmlFor="displayName" error={errors.displayName?.message}>
          <Input id="displayName" autoComplete="name" {...register('displayName')} />
        </FormField>
        <FormField label="Пароль" htmlFor="password" error={errors.password?.message}>
          <Input id="password" type="password" autoComplete="new-password" {...register('password')} />
        </FormField>
        <FormField label="Подтверждение" htmlFor="confirm" error={errors.confirm?.message}>
          <Input id="confirm" type="password" autoComplete="new-password" {...register('confirm')} />
        </FormField>
        {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}
        <Button type="submit" className="w-full" disabled={accept.isPending}>
          {accept.isPending ? <Spinner /> : 'Создать аккаунт'}
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">{children}</CardContent>
      </Card>
    </div>
  );
}
